import {
	get_mode,
	isProAnyEM,
	isRgbCct,
	is_gen_2,
	get_device_component_index,
	isEMWithoutSwitch,
} from 'src/helpers/interface_helpers';
import { get_em_component } from 'src/helpers/emeters_data_helpers';
import { devices_settings } from '../../device-runtime/status';
import type { Device } from 'src/types/Device';

const mesure_types = ['emeters', 'meters', 'rollers'];

export type ConsumptionTotals = {
	in_power: number;
	ex_power: number;
	_power: number;
};

export type DeviceConsumptionResult = ConsumptionTotals & {
	/** When set, room aggregation assigns `_power` from the branch total (legacy EM / pm1 behavior). */
	roomPowerFrom?: 'in_power' | 'ex_power';
};

export type ConsumptionExcludeKey = 'no_account_cons' | 'no_room_cons';

/** Room aggregation preserves legacy gen1 branches; per-device uses the device path. */
export type ConsumptionCalcScope = 'room' | 'device';

export const createEmptyConsumption = (): ConsumptionTotals => ({
	in_power: 0,
	ex_power: 0,
	_power: 0,
});

const calculateEmComponentConsumption = (emStatus) => {
	if (typeof emStatus?.total_act_power !== 'undefined') {
		return emStatus.total_act_power;
	}
	return Object.keys(emStatus)
		.filter((k) => k.indexOf('act_power') !== -1)
		.map((k) => emStatus[k])
		.reduce((a, b) => a + b, 0);
};

const calculateEmDeviceConsumption = (device, status, settings) => {
	const emComponent = get_em_component(device, status, settings);
	const emChannelComponent = emComponent + ':' + device.channel;
	const emStatus = status[emChannelComponent] || {};
	return calculateEmComponentConsumption(emStatus);
};

const getConsumptionGen2StatusPrefix = (device, getMode: string) => {
	let mode = ['relay'].includes(getMode)
		? 'switch'
		: ['roller'].includes(getMode)
			? 'cover'
			: ['cctx2', 'rgbx2light', 'rgbcct'].includes(getMode)
				? device.mode
				: getMode === 'power_meter'
					? 'pm1'
					: getMode;
	if (isRgbCct(device.type)) {
		mode = 'rgbcct';
	}
	return mode;
};

const applyExcludedPower = (
	totals: ConsumptionTotals,
	power: number,
	device: Device,
	excludeKey: ConsumptionExcludeKey,
	assignMode: 'set' | 'add',
) => {
	const excluded = device?.[excludeKey];
	if (!excluded) {
		if (assignMode === 'set') totals.in_power = power;
		else totals.in_power += power;
	} else {
		if (assignMode === 'set') totals.ex_power = power;
		else totals.ex_power += power;
	}
	if (assignMode === 'set') totals._power = power;
	else totals._power += power;
};

const calculateGen2Consumption = (
	device: Device,
	status: any,
	excludeKey: ConsumptionExcludeKey,
	scope: ConsumptionCalcScope,
): DeviceConsumptionResult | null => {
	const settings = devices_settings.getByRealId(device.id);
	const getMode = get_mode(device, status, settings);
	const mode = getConsumptionGen2StatusPrefix(device, getMode);
	const index = get_device_component_index(device);
	const isRoller = device?.category === 'roller';
	const state = isRoller ? status?.[mode + ':' + index]?.state : status?.[mode + ':' + index]?.output;

	const component = status[mode + ':' + index];
	const hasApower =
		scope === 'room'
			? typeof component !== 'undefined' && typeof component.apower !== 'undefined'
			: Boolean(component?.apower);

	if (hasApower) {
		if (!((isRoller && state !== 'stopped') || (!isRoller && (state || mode === 'pm1')))) {
			return null;
		}
		const totals = createEmptyConsumption();
		applyExcludedPower(totals, status[mode + ':' + index].apower, device, excludeKey, 'set');
		return totals;
	}

	if (isProAnyEM(device.type) || isEMWithoutSwitch(device.type)) {
		const emConsumption = calculateEmDeviceConsumption(device, status, settings);
		const totals: DeviceConsumptionResult = createEmptyConsumption();
		applyExcludedPower(totals, emConsumption, device, excludeKey, 'set');
		if (scope === 'room') {
			totals._power = 0;
			totals.roomPowerFrom = device?.[excludeKey] ? 'ex_power' : 'in_power';
		}
		return totals;
	}

	if (device.category === 'power_meter') {
		if (!((isRoller && state !== 'stopped') || (!isRoller && state))) {
			return null;
		}
		const totals: DeviceConsumptionResult = createEmptyConsumption();
		const power = status['pm1:' + device.channel].apower;
		if (!device?.[excludeKey]) {
			totals.in_power += power;
		} else {
			totals.ex_power += power;
		}
		if (scope === 'room') {
			totals._power = 0;
			totals.roomPowerFrom = device?.[excludeKey] ? 'ex_power' : 'in_power';
		} else {
			totals._power = totals.in_power || totals.ex_power;
		}
		return totals;
	}

	return null;
};

const calculateGen1Consumption = (
	device: Device,
	deviceKey: string,
	status: any,
	allDevices: Record<string, Device>,
	excludeKey: ConsumptionExcludeKey,
	scope: ConsumptionCalcScope,
): ConsumptionTotals | null => {
	let totals: ConsumptionTotals | null = null;

	for (const type of mesure_types) {
		if (typeof status[type] === 'undefined') continue;

		if (!totals) totals = createEmptyConsumption();

		if (!device?.[excludeKey]) {
			if (
				allDevices[deviceKey].type === 'SHEM-3' &&
				(typeof allDevices[deviceKey].split === 'undefined' ||
					allDevices[deviceKey].split === false)
			) {
				totals.in_power +=
					status.emeters[0].power + status.emeters[1].power + status.emeters[2].power;
			} else if (allDevices[deviceKey].type === 'SHEM') {
				totals.in_power += status[type][device.channel].power;
			} else if (allDevices[deviceKey].type === 'SHSW-21') {
				totals.in_power += status[type][0].power;
			} else if (isEMWithoutSwitch(allDevices[deviceKey].type)) {
				if (scope === 'room') {
					totals.in_power += status[type][0].power + device[type][1].power;
				} else {
					totals.in_power += status[type][0].power + status[type][1].power;
				}
			} else if (typeof status[type][device.channel] !== 'undefined') {
				if (device.mode === 'roller' && type == device.category + 's') {
					totals.in_power += status['rollers']?.[0].power;
				} else if (status[device.category + 's']?.[device.channel]?.ison) {
					totals.in_power += status[type][device.channel].power;
				}
			}
		} else {
			if (
				allDevices[deviceKey].type === 'SHEM-3' &&
				(typeof allDevices[deviceKey].split === 'undefined' ||
					allDevices[deviceKey].split === false)
			) {
				totals.ex_power +=
					status.emeters[0].power + status.emeters[1].power + status.emeters[2].power;
			} else if (isEMWithoutSwitch(allDevices[deviceKey].type)) {
				if (scope === 'room') {
					totals.ex_power += status.emeters[0].power + status.emeters[1].power;
				} else {
					totals.ex_power += status[type][device.channel].power;
				}
			} else if (allDevices[deviceKey].type === 'SHEM') {
				totals.ex_power += status[type][device.channel].power;
			} else if (allDevices[deviceKey].type === 'SHSW-21') {
				totals.ex_power += status[type][0].power;
			} else if (typeof status[type][device.channel] !== 'undefined') {
				if (device.mode === 'roller' && type == device.category + 's') {
					totals.ex_power += status['rollers']?.[0].power;
				} else if (status[device.category + 's']?.[device.channel]?.ison) {
					totals.ex_power += status[type][device.channel].power;
				}
			}
		}

		if (
			allDevices[deviceKey].type === 'SHEM-3' &&
			(typeof allDevices[deviceKey].split === 'undefined' || allDevices[deviceKey].split === false)
		) {
			totals._power += status.emeters[0].power + status.emeters[1].power + status.emeters[2].power;
		} else if (allDevices[deviceKey].type === 'SHEM') {
			totals._power += status[type][device.channel].power;
		} else if (isEMWithoutSwitch(allDevices[deviceKey].type)) {
			if (scope === 'room') {
				totals._power += status[type][device.channel].power;
			} else {
				totals._power += status[type][0].power + status[type][1].power;
			}
		} else if (allDevices[deviceKey].type === 'SHSW-21') {
			if (scope === 'device') {
				totals.in_power = status[type][0].power;
			}
			totals._power += status[type][0].power;
		} else if (typeof status[type][device.channel] !== 'undefined') {
			if (device.mode === 'roller') {
				totals._power += status['rollers']?.[0].power;
			} else if (status[device.category + 's']?.[device.channel]?.ison) {
				totals._power += status[type][device.channel].power;
			}
		}
	}

	return totals;
};

/** Returns per-device consumption or null when device is offline / not contributing. */
export function calculateDeviceConsumption(
	device: Device,
	deviceKey: string,
	status: any,
	allDevices: Record<string, Device>,
	excludeKey: ConsumptionExcludeKey,
	scope: ConsumptionCalcScope = 'device',
): DeviceConsumptionResult | null {
	if (!status?.online) return null;
	if (typeof device === 'undefined' || device.shared) return null;
	if (typeof status === 'undefined' || typeof deviceKey === 'undefined') return null;

	if (is_gen_2(device)) {
		return calculateGen2Consumption(device, status, excludeKey, scope);
	}

	return calculateGen1Consumption(device, deviceKey, status, allDevices, excludeKey, scope);
}

export const mergeConsumptionInto = (target: ConsumptionTotals, source: ConsumptionTotals) => {
	target.in_power += source.in_power;
	target.ex_power += source.ex_power;
	target._power += source._power;
};
